import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProjectId, Team, UserId } from 'api/models';
import { logger } from 'services';
import {
  addProjectToTeam,
  removeProjectFromTeam,
  updateTeam,
} from 'utils/idb-utils';
import { MembershipsStore, MembershipData } from './models';

interface LoadedActionPayload {
  projectId: ProjectId;
  memberships: MembershipData[];
  placeholders?: Team[];
}

interface RemoveActionPayload {
  projectId: ProjectId;
  userIds: UserId[];
}

export const membershipsSlice = createSlice({
  name: '@@MEMBERSHIPS',
  initialState: MembershipsStore.empty(),
  reducers: {
    setLoading: (
      state: MembershipsStore,
      { payload: projectId }: PayloadAction<ProjectId>,
    ) => MembershipsStore.setLoading(projectId)(state),
    setLoaded: (
      state: MembershipsStore,
      {
        payload: { projectId, memberships, placeholders },
      }: PayloadAction<LoadedActionPayload>,
    ) => MembershipsStore.load(projectId, memberships, placeholders)(state),
    setLoadedSeveral: (
      state: MembershipsStore,
      { payload: loaded }: PayloadAction<LoadedActionPayload[]>,
    ) =>
      loaded.reduce(
        (store, { projectId, memberships, placeholders }) =>
          MembershipsStore.load(projectId, memberships, placeholders)(store),
        state,
      ),
    add: (
      state: MembershipsStore,
      {
        payload: { projectId, memberships },
      }: PayloadAction<LoadedActionPayload>,
    ) => MembershipsStore.add(projectId, memberships)(state),
    remove: (
      state: MembershipsStore,
      { payload: { projectId, userIds } }: PayloadAction<RemoveActionPayload>,
    ) => MembershipsStore.remove(projectId, userIds)(state),

    addProjectPlaceholder: (
      state: MembershipsStore,
      {
        payload: { projectId, team },
      }: PayloadAction<{ projectId: ProjectId; team: Team }>,
    ) => {
      addProjectToTeam({
        projectId: projectId[1],
        teamId: team.teamId,
      }).catch((err) => {
        logger.error(
          `Error occured while adding projectId to team. teamId: ${team?.teamId}`,
          err,
        );
      });

      MembershipsStore.addProjectPlaceholder(projectId, team)(state);
    },

    updateProjectPlaceholder: (
      state: MembershipsStore,
      {
        payload: { projectId, team },
      }: PayloadAction<{ projectId: ProjectId; team: Team }>,
    ) => {
      updateTeam(team).catch((err) => {
        logger.error(
          `Error occured while updating team. teamId: ${team?.teamId}`,
          err,
        );
      });
      MembershipsStore.updateProjectPlaceholder(projectId, team)(state);
    },

    removeProjectPlaceholder: (
      state: MembershipsStore,
      {
        payload: { projectId, teamId },
      }: PayloadAction<{ projectId: ProjectId; teamId: number }>,
    ) => {
      removeProjectFromTeam({
        projectId: projectId[1],
        teamId,
      }).catch((err) => {
        logger.error(
          `Error occured while removing projectId from team. teamId: ${teamId}`,
          err,
        );
      });
      MembershipsStore.removeProjectPlaceholder(projectId, teamId)(state);
    },
  },
});
